import React, { useCallback, useState } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  DataTable,
  HStack,
  NoDataMessage,
  Pagination,
  TextField,
  useDateTimeContext,
  VStack,
} from '@rtkwlf/fenrir-react';
import { faSearch } from '@rtkwlf/pro-solid-svg-icons/faSearch';
import type { SchedulePage } from '@rtkwlf/rendall';
import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

import { useGetScannerScanSchedules } from '@apis/rendall/queries/use-get-scanner-scan-schedules';
import { GeneralWrapper } from '@packages/components/generalWrapper/GeneralWrapper';

import { prepareScanningScheduleData } from './prepare-scanning-schedule-data';
import { useScanningScheduleColumns } from './use-scanning-schedule-columns/use-scanning-schedule-columns';

const SKELETON_ROWS = 2;

export const ScanningScheduleTable: React.FC<{ scannerId: string }> = ({
  scannerId,
}) => {
  const { t } = useTranslation('telemetryScannerConfig');
  const { timezone } = useDateTimeContext();

  const [globalFilter, setGlobalFilter] = useState<string>('');

  const { data, isError, isFetching, isRefetching } =
    useGetScannerScanSchedules(scannerId || '', {
      select: useCallback(
        (data: SchedulePage) => {
          return prepareScanningScheduleData(data, timezone);
        },
        [timezone],
      ),
      refetchOnMount: true,
    });

  const table = useReactTable({
    columns: useScanningScheduleColumns(),
    data: data ?? [],
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      globalFilter,
    },
  });

  const totalMatchedRecords = table.getPrePaginationRowModel().rows.length;

  return (
    <GeneralWrapper isError={isError} width="full">
      <VStack gap="default" width="full" alignItems="flex-end">
        {!isError && (
          <HStack alignSelf="flex-start">
            <TextField
              type="text"
              placeholder={t('components:filter.placeholder.search')}
              fontSize="default"
              fontWeight="normal"
              iconRight={<FontAwesomeIcon icon={faSearch} />}
              onChange={(e) => {
                setGlobalFilter(e.target.value as string);
              }}
            />
          </HStack>
        )}

        <DataTable
          table={table}
          skeletonRows={isFetching || isRefetching ? SKELETON_ROWS : undefined}
          stickyLastColumn
        />

        {!(isFetching || isRefetching) &&
          table.getFilteredRowModel().flatRows.length === 0 && (
            <VStack xAlign="center" width="full" paddingY="default">
              <NoDataMessage table={table} />
            </VStack>
          )}

        {totalMatchedRecords > 0 && (
          <Pagination table={table} totalRows={totalMatchedRecords} />
        )}
      </VStack>
    </GeneralWrapper>
  );
};
